/**
 * 企微聊天侧边栏（顾问陪跑 PR⑦，草案 §8 S-1～S-4）—— 只有带客户的人（销售、运营主管）能看到，客户永远看不到。
 *
 *   S-1 识别：企微静默授权识别是哪个销售（admin_users.wecom_userid）；getCurExternalContact 识别当前客户
 *   S-2 首次绑定：这位联系人还没对应到档案 → 列出名下未绑定的档案供选择
 *   S-3 待发列表：订单 / 定稿确认 / 阅读 / 话题卡片（服务端按真实数据算出）；
 *       话题卡片按这本书的进度给出「今日话题」推荐（P2-3）：今天发几个、先发哪个、哪个今天已经发过
 *   S-4 发送：sendChatMessage 把小程序卡片插进聊天框，销售在企微里点发送
 *
 * 不在主布局里、不走扫码登录：侧边栏是企微内置浏览器里的独立网页。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Empty, Input, Popconfirm, Result, Space, Spin, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../stores/session';
import { getWecomAuthorizeUrl, loginWithWecomCode } from '../../api/wecom';
import {
  bindWecomContact,
  getWecomContext,
  reportAdvisorCardSent,
  type AdvisorCardDto,
  type AdvisorCardType,
  type TodayPlanDto,
} from '../../api/advisor';
import { getCurrentExternalUserId, isWecomBrowser, sendMiniprogramCard } from '../../utils/wecom-jssdk';
import { narratorsText } from '../advisor/customers/narrator';
import { planHeadline, planNotes, sentText, splitTodayCards, topicWorkIds, type Tone } from '../advisor/customers/today-plan';

const { Text, Title } = Typography;

const STATE_KEY = 'wecom-oauth-state';
// 与 admin-service auth.routes 的 WECOM_SIDEBAR_ROLES 同口径：带客户的人（销售、运营主管）+ 超管排障
const SIDEBAR_ROLES = new Set(['sales', 'ops_lead', 'super_admin']);

const TYPE_META: Record<AdvisorCardType, { label: string; color: string }> = {
  order: { label: '订单', color: 'gold' },
  finalize: { label: '定稿确认', color: 'red' },
  reading: { label: '阅读', color: 'green' },
  topic: { label: '话题', color: 'blue' },
};
const TYPE_ORDER: AdvisorCardType[] = ['order', 'finalize', 'reading', 'topic'];

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

/** 仅本地开发：普通浏览器里没有企微环境，用 ?mockExternalUserId= 模拟当前聊天对象 */
function devMockExternalUserId(): string | null {
  // 本项目未引 vite/client 类型；Vite 构建时会把 import.meta.env 静态替换，生产里 DEV=false
  if (!(import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) return null;
  return new URLSearchParams(window.location.search).get('mockExternalUserId');
}

type Phase =
  | { kind: 'auth' }
  | { kind: 'error'; message: string }
  | { kind: 'not_wecom' }
  | { kind: 'ready'; externalUserId: string };

/** 同一个 code 的换会话请求只发一次（重渲染 / 并发 effect 复用同一个 Promise） */
const inflight = new Map<string, ReturnType<typeof loginWithWecomCode>>();
let exchanging: Promise<unknown> | null = null;
function exchangeOnce(code: string) {
  let p = inflight.get(code);
  if (!p) {
    p = loginWithWecomCode(code);
    inflight.set(code, p);
    const done = p.then(
      () => undefined,
      () => undefined,
    );
    exchanging = done;
    void done.then(() => {
      if (exchanging === done) exchanging = null;
    });
  }
  return p;
}

export default function WecomSidebarPage() {
  const { accessToken, role, setSession } = useSession();
  const [phase, setPhase] = useState<Phase>({ kind: 'auth' });

  // S-1：登录 → 识别当前聊天对象
  useEffect(() => {
    let alive = true;
    (async () => {
      const mock = devMockExternalUserId();
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      try {
        // 换会话请求还在路上时 effect 被重跑：地址栏里的 code 已摘掉，别误判成「没登录」再跳转授权
        if (!code && exchanging) {
          await exchanging;
          return;
        }
        if (code) {
          const expected = sessionStorage.getItem(STATE_KEY);
          const stateOk = !expected || params.get('state') === expected;
          sessionStorage.removeItem(STATE_KEY);
          // code 只能用一次：发出前先从地址栏摘掉，失败后刷新也不会把用过的 code 再发一遍
          window.history.replaceState(null, '', '/wecom/sidebar');
          if (!stateOk) throw new Error('授权状态不匹配，请重新打开侧边栏');
          const s = await exchangeOnce(code);
          setSession({ actorId: s.actorId, role: s.role, accessToken: s.accessToken });
          return; // 会话写入后 accessToken 变化，本 effect 重跑进入下一步
        }
        if (!accessToken || !role || !SIDEBAR_ROLES.has(role)) {
          if (!isWecomBrowser() && !mock) {
            if (alive) setPhase({ kind: 'not_wecom' });
            return;
          }
          if (mock) throw new Error('本地模拟需先登录后台（销售账号）');
          const state = Math.random().toString(36).slice(2, 12);
          sessionStorage.setItem(STATE_KEY, state);
          window.location.href = await getWecomAuthorizeUrl(`${window.location.origin}/wecom/sidebar`, state);
          return;
        }
        const externalUserId = mock ?? (await getCurrentExternalUserId());
        if (alive) setPhase({ kind: 'ready', externalUserId });
      } catch (e) {
        if (alive) setPhase({ kind: 'error', message: errMsg(e, '侧边栏初始化失败') });
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, role, setSession]);

  if (phase.kind === 'auth') {
    return (
      <div style={pageStyle}>
        <Spin tip="正在识别…" fullscreen={false}>
          <div style={{ height: 120 }} />
        </Spin>
      </div>
    );
  }
  if (phase.kind === 'not_wecom') {
    return (
      <div style={pageStyle}>
        <Result status="info" title="请在企业微信里打开" subTitle="这是企业微信聊天侧边栏，只能在和客户的单聊里使用。" />
      </div>
    );
  }
  if (phase.kind === 'error') {
    return (
      <div style={pageStyle}>
        <Result
          status="warning"
          title="打不开侧边栏"
          subTitle={phase.message}
          extra={<Button onClick={() => window.location.replace('/wecom/sidebar')}>重试</Button>}
        />
      </div>
    );
  }
  return <SidebarContent externalUserId={phase.externalUserId} />;
}

const pageStyle: React.CSSProperties = { padding: 12, maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#fafafa' };

function SidebarContent({ externalUserId }: { externalUserId: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['wecom-context', externalUserId];
  const ctxQuery = useQuery({ queryKey, queryFn: () => getWecomContext(externalUserId), retry: false });
  const ctx = ctxQuery.data;

  const bindMutation = useMutation({
    mutationFn: (customerId: string) => bindWecomContact(customerId, externalUserId),
    onSuccess: () => {
      message.success('已绑定');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e) => {
      message.error(errMsg(e, '绑定失败'));
    },
  });

  if (ctxQuery.isLoading) {
    return (
      <div style={pageStyle}>
        <Card loading />
      </div>
    );
  }
  if (ctxQuery.isError || !ctx) {
    return (
      <div style={pageStyle}>
        <Alert type="error" showIcon message={errMsg(ctxQuery.error, '加载失败')} />
      </div>
    );
  }

  if (ctx.status === 'taken') {
    return (
      <div style={pageStyle}>
        <Result status="info" title="这位客户由其他顾问负责" subTitle="如需接手，请联系管理员在后台转交。" />
      </div>
    );
  }

  // S-2：首次绑定
  if (ctx.status === 'unbound') {
    return (
      <div style={pageStyle}>
        <Title level={5}>这位联系人还没对应到客户档案</Title>
        <Text type="secondary">从你名下还没绑定的档案里选一个；没有的话先在后台建档。</Text>
        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 12 }}>
          {ctx.candidates.length === 0 ? (
            <Empty description="名下没有待绑定的档案" />
          ) : (
            ctx.candidates.map((c) => (
              <Card key={c.customerId} size="small">
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space direction="vertical" size={0}>
                    <Text strong>{c.displayName}</Text>
                    <Text type="secondary">讲述人 {narratorsText(c.narrators)}</Text>
                  </Space>
                  <Popconfirm title={`把当前联系人绑定到「${c.displayName}」？`} onConfirm={() => bindMutation.mutate(c.customerId)}>
                    <Button type="primary" size="small" loading={bindMutation.isPending}>
                      就是这位
                    </Button>
                  </Popconfirm>
                </Space>
              </Card>
            ))
          )}
        </Space>
      </div>
    );
  }

  // S-3 / S-4
  return (
    <div style={pageStyle}>
      <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          {ctx.customer.displayName}
        </Title>
        <Text type="secondary">讲述人 {narratorsText(ctx.customer.narrators)}</Text>
      </Space>
      {!ctx.miniprogramAppId && <Alert type="error" showIcon message="小程序 AppID 未配置（WECOM_MINIPROGRAM_APPID），暂时不能发卡片" />}
      <CardList
        customerId={ctx.customer.customerId}
        appId={ctx.miniprogramAppId}
        cards={ctx.cards}
        plans={ctx.plans ?? []}
        // 发出后重新取一次：话题卡要立刻标上「今天已发」，并占掉今天的名额
        onSent={() => void queryClient.invalidateQueries({ queryKey })}
      />
    </div>
  );
}

interface CardListProps {
  customerId: string;
  appId: string;
  cards: AdvisorCardDto[];
  plans: TodayPlanDto[];
  onSent: () => void;
}

export function CardList({ cards, ...rest }: CardListProps) {
  // 一位客户给爸妈各做一本时，两本的话题卡标题一模一样，按书（讲述人）再分一层，免得发错
  const sections = useMemo(() => {
    const labels = [...new Set(cards.map((c) => c.bookLabel))];
    return labels.length > 1
      ? labels.map((label) => ({ label, cards: cards.filter((c) => c.bookLabel === label) }))
      : [{ label: null as string | null, cards }];
  }, [cards]);
  if (cards.length === 0) return <Empty description="暂时没有可发的卡片（客户还没付款开书）" />;
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {sections.map((sec) => (
        <BookSection key={sec.label ?? 'only'} label={sec.label} cards={sec.cards} {...rest} />
      ))}
    </Space>
  );
}

const TONE_TYPE: Record<Tone, 'secondary' | 'warning' | 'danger' | 'success'> = {
  muted: 'secondary',
  warn: 'warning',
  danger: 'danger',
  success: 'success',
};

/** 一本书的卡片：订单 / 定稿 → 今日话题 → 阅读 → 其他话题（折叠） */
function BookSection({
  label,
  cards,
  customerId,
  appId,
  plans,
  onSent,
}: CardListProps & { label: string | null }) {
  const send = { customerId, appId, onSent };
  const byType = (type: AdvisorCardType) => cards.filter((c) => c.type === type);
  // 这一节里每本书的话题卡（正常一节就一本书；同名的两本书也各算各的）
  const works = topicWorkIds(cards).map((workId) => ({
    workId,
    plan: plans.find((p) => p.workId === workId),
    ...splitTodayCards(cards.filter((c) => c.type === 'topic' && c.workId === workId)),
  }));

  const renderCards = (list: AdvisorCardDto[]) =>
    list.map((c) => <SendableCard key={c.key} card={c} {...send} />);

  return (
    <div data-book-label={label ?? undefined}>
      {label && (
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          {label}
        </Text>
      )}
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {(['order', 'finalize'] as const).map((type) =>
          byType(type).length > 0 ? (
            <div key={type}>
              <Text type="secondary">{TYPE_META[type].label}</Text>
              <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 4 }}>
                {renderCards(byType(type))}
              </Space>
            </div>
          ) : null,
        )}

        {works.map((w) =>
          w.plan ? (
            <div key={`today-${w.workId}`} data-today-plan={w.workId}>
              <Text strong>{planHeadline(w.plan)}</Text>
              <div>
                {planNotes(w.plan).map((n) => (
                  <Text key={n.text} type={TONE_TYPE[n.tone]} style={{ display: 'block', fontSize: 12 }}>
                    {n.text}
                  </Text>
                ))}
              </div>
              {w.recommended.length > 0 ? (
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
                  {renderCards(w.recommended)}
                </Space>
              ) : (
                w.plan.remainingTopics > 0 && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    今天建议发的话题已经发过了
                  </Text>
                )
              )}
            </div>
          ) : (
            // 读不到进度、没有计划：话题卡平铺（与改版前一致）
            <div key={`topics-${w.workId}`}>
              <Text type="secondary">{TYPE_META.topic.label}</Text>
              <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 4 }}>
                {renderCards(w.otherTopics)}
              </Space>
            </div>
          ),
        )}

        {byType('reading').length > 0 && (
          <div>
            <Text type="secondary">{TYPE_META.reading.label}</Text>
            <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 4 }}>
              {renderCards(byType('reading'))}
            </Space>
          </div>
        )}

        {works.map(
          (w) =>
            w.plan &&
            w.otherTopics.length > 0 && (
              <Collapse
                key={`others-${w.workId}`}
                size="small"
                items={[
                  {
                    key: 'others',
                    label: `其他话题（${w.otherTopics.length}）`,
                    children: (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {renderCards(w.otherTopics)}
                      </Space>
                    ),
                  },
                ]}
              />
            ),
        )}
      </Space>
    </div>
  );
}

function SendableCard({
  customerId,
  appId,
  card,
  onSent,
}: {
  customerId: string;
  appId: string;
  card: AdvisorCardDto;
  onSent?: () => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [sending, setSending] = useState(false);
  const canSend = !!appId && !!card.imgUrl && title.trim().length > 0;
  const sent = sentText(card);

  const send = useCallback(async () => {
    setSending(true);
    try {
      const msg = { appid: appId, title: title.trim(), imgUrl: card.imgUrl, page: card.page, query: card.query };
      let sent: boolean;
      if (devMockExternalUserId()) {
        // 本地模拟：记下要发的消息，便于检查（生产构建无此分支）
        (window as any).__wecomSent = [...((window as any).__wecomSent ?? []), msg];
        sent = true;
      } else {
        sent = await sendMiniprogramCard(msg);
      }
      if (sent) {
        message.success('已放进聊天框，在企业微信里点发送即可');
        // 回报成功后刷新（话题卡标「今天已发」）；回报失败不打扰销售，卡片已经在聊天框里了
        reportAdvisorCardSent(customerId, card.key)
          .then(() => onSent?.())
          .catch(() => {});
      }
    } catch (e) {
      message.error(errMsg(e, '发送失败'));
    } finally {
      setSending(false);
    }
  }, [appId, card, customerId, onSent, title]);

  return (
    <Card size="small" data-card-key={card.key}>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space size={4} wrap>
          <Tag color={TYPE_META[card.type].color}>{TYPE_META[card.type].label}</Tag>
          <Text type="secondary">{card.subtitle}</Text>
          {card.recommendation && <Tag color="geekblue">{card.recommendation.reason}</Tag>}
          {sent === '今天已发' ? <Tag color="green">{sent}</Tag> : sent && <Text type="secondary">{sent}</Text>}
        </Space>
        <Input.TextArea
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={30}
          autoSize={{ minRows: 1, maxRows: 3 }}
          aria-label="卡片标题"
        />
        {!card.imgUrl && <Text type="warning">卡片封面图未配置（ADVISOR_CARD_IMG_*），暂时不能发送</Text>}
        <Button type="primary" block disabled={!canSend} loading={sending} onClick={() => void send()}>
          发送
        </Button>
      </Space>
    </Card>
  );
}
